import * as CookieConsent from 'vanilla-cookieconsent';
import { useEffect } from 'react';
import '@orestbida/iframemanager';
import { useIntl } from 'react-intl';

export const CookieConsentComponent = ({ pathname }) => {
  const localization = useIntl();

  useEffect(() => {
    const cookieConsentConfig = {
      categories: {
        necessary: {
          enabled: true, // this category is enabled by default
          readOnly: true, // this category cannot be disabled
        },
        analytics: {},
        thirdpartycookies: {},
      },

      language: {
        autoDetect: 'document',
        default: 'de',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies!',
              description: '',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage Individual preferences',
            },
            preferencesModal: {
              title: 'Manage cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Accept current selection',
              closeIconLabel: 'Close modal',
              sections: [
                {
                  title: 'Use of cookies',
                  description:
                    'We use cookies to ensure the basic functions of the website and to improve your online experience. You can choose to opt in or out for each category. For more details on cookies and other sensitive data, please read the full <a href="en/privacy policy" target="_blank">privacy policy</a>.',
                },
                {
                  title: 'Strictly Necessary cookies',
                  description:
                    'These cookies are essential for the proper functioning of the website and cannot be disabled.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Third Party Cookies',
                  linkedCategory: 'thirdpartycookies',
                  cookieTable: {
                    headers: {
                      name: 'Name',
                      domain: 'Service',
                      description: 'Description',
                    },
                    body: [
                      {
                        name: 'cc_openstreetmap',
                        domain: 'Openstreetmap',
                        description:
                          'When using openstreetmap, personal data such as your IP address or browser type is transmitted to the Openstreetmap servers. This is necessary to display the map. You can revoke this consent at any time, but you will no longer be able to use the functions of Openstreetmap. ',
                      },
                    ],
                  },
                },
                {
                  title: 'Performance and Analytics',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Name',
                      domain: 'Service',
                      description: 'Description',
                    },
                    body: [
                      {
                        name: 'cc_matomo',
                        domain: 'Matomo',
                        description:
                          'These cookies collect information about how you use our website. All data is anonymized and cannot be used to identify you. The web analysis service Matomo is hosted on our own Austrian servers and no information is passed on to third parties.',
                      },
                    ],
                  },
                },
                {
                  title: 'More information',
                  description:
                    'For any queries in relation to our policy on cookies and your choices, please <a href="/en/imprint" target="_blank">contact us</a>',
                },
              ],
            },
          },
          de: {
            consentModal: {
              title: 'Wir nutzen Cookies!',
              description: '',
              acceptAllBtn: 'Alle akzeptieren',
              acceptNecessaryBtn: 'Alle ablehnen',
              showPreferencesBtn: 'Individuelle Einstellungen verwalten',
            },
            preferencesModal: {
              title: 'Cookie Einstellungen verwalten',
              acceptAllBtn: 'Alle akzeptieren',
              acceptNecessaryBtn: 'Alle ablehnen',
              savePreferencesBtn: 'Aktuelle Auswahl akzeptieren',
              closeIconLabel: 'Fenster schließen',
              sections: [
                {
                  title: 'Verwendung von Cookies',
                  description:
                    'Wir verwenden Cookies, um die grundlegenden Funktionen der Website zu gewährleisten und um Ihr Online-Erlebnis zu verbessern. Sie können für jede Kategorie wählen, ob Sie sich ein- oder austragen möchten. Für weitere Einzelheiten zu Cookies und anderen sensiblen Daten lesen Sie bitte die vollständige <a href="/de/datenschutz" target="_blank">Datenschutzerklärung</a>.',
                },
                {
                  title: 'Unbedingt erforderliche Cookies',
                  description:
                    'Diese Cookies sind für das reibungslose Funktionieren der Website unerlässlich. Ohne diese Cookies würde die Website nicht richtig funktionieren.',

                  //this field will generate a toggle linked to the 'necessary' category
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Drittanbietercookies',
                  linkedCategory: 'thirdpartycookies',
                  cookieTable: {
                    headers: {
                      name: 'Name',
                      domain: 'Service',
                      description: 'Beschreibung',
                    },
                    body: [
                      {
                        name: 'cc_openstreetmap',
                        domain: 'Openstreetmap',
                        description:
                          'Bei Verwendung von openstreetmap werden personenbezogene Daten wie z.B. Ihre IP-Adresse oder Ihr Browsertyp an die Server von Openstreetmap übermittelt. Dies ist für das Anzeigen von der Karte erforderlich. Sie können diese Einwilligung jederzeit widerrufen, wodurch Sie die Funktionen von Openstreetmap aber nicht mehr nutzen können. Durch Einwilligung in die Verwendung von Drittanbietercookies stimmen Sie der Übermittlung dieser Daten zu (Art. 49 Abs. 1 lit. a DSGVO).',
                      },
                    ],
                  },
                },
                {
                  title: 'Analytische und Performance-Cookies',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: {
                      name: 'Name',
                      domain: 'Service',
                      description: 'Beschreibung',
                    },
                    body: [
                      {
                        name: 'cc_matomo',
                        domain: 'Matomo',
                        description:
                          'Diese Cookies sammeln Informationen darüber, wie Sie unsere Website nutzen. Alle Daten sind anonymisiert und können nicht dazu verwendet werden, Sie zu identifizieren. Der Webanalysedienst Matomo wird von uns selbst auf österreichischen Servern gehostet und es werden keine Informationen an Dritte weitergegeben.',
                      },
                    ],
                  },
                },

                {
                  title: 'Weitere Informationen',
                  description:
                    'Sollten Sie weitere Fragen betreffend dieser Auswahl haben, können Sie uns jederzeit gerne <a href="/de/impressum" target="_blank">kontakieren</a>',
                },
              ],
            },
          },
        },
      },
    };

    const imConfig = {
      currLang: localization.locale,

      onChange: ({ changedServices, eventSource }) => {
        if (eventSource.type === 'click') {
          // Retrieve all accepted services:
          // const allAcceptedServices = im.getState().acceptedServices;

          /**
           * Retrieve array of already accepted services
           * and add the new service
           */
          const servicesToAccept = [
            ...CookieConsent.getUserPreferences().acceptedServices['targeting'], //cookieconsent v3
            ...changedServices,
          ];

          CookieConsent.acceptService(servicesToAccept, 'targeting');
        }
      },
      services: {
        openstreetmap: {
          embedUrl:
            'https://www.openstreetmap.org/export/embed.html?bbox=14.28521990776062%2C48.301322214422676%2C14.28876042366028%2C48.3028976745941&amp;layer=mapnik&amp;marker=48.302109058479985%2C14.28699016571045',
          iframe: {
            allow:
              'accelerometer; encrypted-media; gyroscope; picture-in-picture;',
          },
          cookie: {
            name: 'cc_openstreetmap',
          },
          languages: {
            de: {
              notice:
                'Die Karte wird von einem Drittanbieter gehostet. Wenn Sie die Karte anzeigen lassen, akzeptieren Sie die <a rel="noreferrer" href="https://osmfoundation.org/wiki/Privacy_Policy" title="Nutzungsbestimmungen" target="_blank">Nutzungsbestimmungen</a> von openstreetmap.org.',
              loadBtn: 'Karte laden',
              loadAllBtn: 'Nicht erneut fragen',
            },

            en: {
              notice:
                'The map is hosted by a third-party provider. By displaying the map, you accept <a rel="noreferrer" href="https://osmfoundation.org/wiki/Privacy_Policy" title="the terms of use" target="_blank">the terms of use</a> of openstreetmap.org.',
              loadBtn: 'Load map',
              loadAllBtn: 'Do not ask again',
            },
          },
        },
      },
    };

    // eslint-disable-next-line no-undef
    const im = iframemanager();

    im.run(imConfig);
    CookieConsent.run(cookieConsentConfig);

    return () => im.reset(true);
  }, [localization.locale]);

  return <></>;
};
